import { Track } from 'livekit-client';
import * as React from 'react';
import {
  ChatIcon,
  ChatToggle,
  TrackToggle,
  useLocalParticipantPermissions,
  useMaybeLayoutContext,
  usePersistentUserChoices,
  StartMediaButton,
} from '@livekit/components-react';

import { notify } from 'gossamer-front-end';
import { MediaDeviceMenu } from '../MediaDeviceMenu/MediaDeviceMenu';

/** @public */
export type ViewerControlBarControls = {
  microphone?: boolean;
  chat?: boolean;
};

/** @public */
export interface ViewerControlBarProps extends React.HTMLAttributes<HTMLDivElement> {
  onDeviceError?: (error: { source: Track.Source; error: Error }) => void;
  variation?: 'minimal' | 'verbose' | 'textOnly';
  controls?: ViewerControlBarControls;
  saveUserChoices?: boolean;
}

export function ViewerControlBar({
  variation,
  controls,
  saveUserChoices = true,
  onDeviceError,
}: ViewerControlBarProps) {
  const [, setIsChatOpen] = React.useState(false);
  const layoutContext = useMaybeLayoutContext();
  React.useEffect(() => {
    if (layoutContext?.widget.state?.showChat !== undefined) {
      setIsChatOpen(layoutContext?.widget.state?.showChat);
    }
  }, [layoutContext?.widget.state?.showChat]);

  const defaultVariation = 'minimal';
  variation ??= defaultVariation;

  const visibleControls = { leave: true, ...controls };

  const localPermissions = useLocalParticipantPermissions();

  if (!localPermissions) {
    visibleControls.chat = false;
    visibleControls.microphone = false;
  } else {
    visibleControls.microphone ??= localPermissions.canPublish;
    visibleControls.chat ??= localPermissions.canPublishData && controls?.chat;
  }

  const showIcon = React.useMemo(() => variation === 'minimal' || variation === 'verbose', [variation]);
  const showText = React.useMemo(() => variation === 'textOnly' || variation === 'verbose', [variation]);

  const { saveAudioInputEnabled, saveAudioInputDeviceId } = usePersistentUserChoices({ preventSave: !saveUserChoices });

  const microphoneOnChange = React.useCallback(
    (enabled: boolean, isUserInitiated: boolean) => (isUserInitiated ? saveAudioInputEnabled(enabled) : null),
    [saveAudioInputEnabled],
  );

  return (
    <div className="lk-control-bar ">
      {visibleControls.microphone && (
        <>
          <StartMediaButton label="Allow media playback" />
          <div className="lk-button-group">
            <TrackToggle
              source={Track.Source.Microphone}
              showIcon={showIcon}
              onChange={microphoneOnChange}
              onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Microphone, error })}
            >
              {showText && 'Microphone'}
            </TrackToggle>
            <div className="lk-button-group-menu">
              <MediaDeviceMenu
                exactMatch={true}
                kind="audioinput"
                onDeviceSelectError={() => {
                  notify(['Audio input device change failed. Please try again.', 'danger']);
                }}
                onActiveDeviceChange={(_kind, deviceId) => {
                  notify(['Your audio input has been changed.', 'info']);
                  saveAudioInputDeviceId(deviceId ?? 'default');
                }}
              />
            </div>
          </div>
        </>
      )}

      {visibleControls.chat && (
        <ChatToggle>
          {showIcon && <ChatIcon />}
          {showText && 'Chat'}
        </ChatToggle>
      )}
    </div>
  );
}
